"use client";

import { useState } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@workspace/ui/components/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@workspace/ui/components/form";
import { Input } from "@workspace/ui/components/input";
import { Button } from "@workspace/ui/components/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@workspace/ui/components/select";
import { api, RouterOutputs } from "@/trpc/react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const externalUrlSchema = z
  .string()
  .url({ message: "Must be a valid URL" })
  .refine((u) => /^https?:\/\//.test(u), { message: "Must start with http:// or https://" });

const internalPathSchema = z
  .string()
  .min(1)
  .max(2048)
  .refine((p) => p.startsWith('/') && !p.startsWith('//'), { message: "Internal path must start with '/'" });

const linkSchema = z
  .object({
    name: z.string().min(1, "Name is required").max(100, "Name is too long"),
    url: z.union([externalUrlSchema, internalPathSchema]),
    instagramUrl: z.string().url("Must be a valid URL").optional().or(z.literal("")),
    tiktokUrl: z.string().url("Must be a valid URL").optional().or(z.literal("")),
    description: z.string().max(1000, "Description is too long").optional().or(z.literal("")),
    featured: z.boolean().default(false),
    categoryId: z.string().optional(),
  })
  .refine((data) => data.featured || !!(data.categoryId && data.categoryId.length > 0), {
    message: "Category is required unless Featured",
    path: ["categoryId"],
  });

type LinkFormData = z.input<typeof linkSchema>;

interface AddLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: Array<{ id: string; name: string }>;
  defaultCategoryId?: string;
  defaultFeatured?: boolean;
}

export function AddLinkDialog({
  open,
  onOpenChange,
  categories,
  defaultCategoryId,
  defaultFeatured = false,
}: AddLinkDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const utils = api.useUtils();
  
  const form = useForm<LinkFormData>({
    resolver: zodResolver(linkSchema),
    defaultValues: {
      name: "",
      url: "",
      instagramUrl: "",
      tiktokUrl: "",
      description: "",
      featured: defaultFeatured,
      categoryId: defaultCategoryId || "",
    },
    mode: "onBlur",
  });
  
  const createLink = api.link.create.useMutation({
    onMutate: async (newLink) => {
      await utils.link.getAll.cancel();
      const previousLinks = utils.link.getAll.getData();
      
      const categoryId = newLink.featured ? null : newLink.categoryId ?? null;
      const optimisticLink: RouterOutputs['link']['getAll'][number] = {
        id: `temp-${Date.now()}`,
        name: newLink.name,
        url: newLink.url,
        instagramUrl: newLink.instagramUrl ?? null,
        tiktokUrl: newLink.tiktokUrl ?? null,
        categoryId,
        description: newLink.description ?? null,
        featured: newLink.featured ?? false,
        deletedAt: null,
        order: 999,
        category: utils.category.getAll.getData()?.find((c) => c.id === categoryId) ?? null,
      };
      
      utils.link.getAll.setData(undefined, (old) => [...(old || []), optimisticLink]);
      
      return { previousLinks };
    },
    onError: (_err, _newLink, context) => {
      utils.link.getAll.setData(undefined, context?.previousLinks);
      toast.error("Failed to create link");
    },
    onSuccess: () => {
      toast.success("Link created successfully");
      form.reset();
      onOpenChange(false);
    },
    onSettled: () => {
      utils.link.getAll.invalidate();
      setIsSubmitting(false);
    },
  });
  
  const onSubmit: SubmitHandler<LinkFormData> = async (data) => {
    setIsSubmitting(true);
    const payload = {
      name: data.name,
      url: data.url,
      instagramUrl: data.instagramUrl || undefined,
      tiktokUrl: data.tiktokUrl || undefined,
      description: data.description?.trim() ? data.description : undefined,
      featured: !!data.featured,
      categoryId: data.featured ? undefined : data.categoryId,
    };
    createLink.mutate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add New Link</DialogTitle>
          <DialogDescription>
            Add a new resource to your collection
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>URL or Internal Path</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://example.com or /internal/path"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Resource name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="instagramUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Instagram URL (Optional)</FormLabel>
                  <FormControl>
                    <Input
                      type="url"
                      placeholder="https://example.com/instagram"
                      {...field}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="tiktokUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>TikTok URL (Optional)</FormLabel>
                  <FormControl>
                    <Input
                      type="url"
                      placeholder="https://example.com/tiktok"
                      {...field}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <textarea
                      placeholder="Add a short description for the featured section"
                      className="min-h-20 w-full resize-y rounded-md border bg-background px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      {...field}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="featured"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <FormLabel className="!mb-0">Featured (no category)</FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="categoryId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value} disabled={isSubmitting || !!form.watch("featured")}> 
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categories && categories.length > 0 ? (
                        categories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-2 text-sm text-muted-foreground">
                          No categories available
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Add Link
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
